import type {
  UnitInfoMobileConeInterface,
  UnitInfoMobileHSIInterface,
  UnitInfoMobileJawAndConeInterface,
  UnitInfoMobileJawInterface,
  UnitInfoMobileScreenInterface,
  UnitInfoMobileVSIInterface,
} from 'store/units/types';
import { UnitTypes } from 'unitData/types';
import { getConventionalScreen1, getGenericScreen1 } from './utils';

type SimulationUnit = { type: string } & Record<string, unknown>;
type SimulationUnits = Record<string, SimulationUnit>;

type MobileRequestUnitInfo = {
  modelName: string;
  configKey: number;
  moisture?: number;
  newMoisture: boolean;
  units: SimulationUnits;
};

const filterDecks = <T>(
  decks: Record<string, T>,
  i: number[],
): Record<string, T> =>
  Object.keys(decks)
    .filter((key) => i.includes(parseInt(key, 10)))
    .reduce((cur, key) => {
      return Object.assign(cur, { [key]: decks[key] });
    }, {});

export const mobileTrackedConeRequest = (
  unitInfo: UnitInfoMobileConeInterface,
) => {
  const isUS = unitInfo.modelName.includes('US');
  const isCS = isUS || unitInfo.modelName.includes('QS');

  const crusherType = isCS
    ? UnitTypes.CONE_CRUSHER_CS
    : UnitTypes.CONE_CRUSHER_CH;

  const trackedCone1 = {
    type: crusherType,
    css: unitInfo.CONE1.css,
    unitThrow: unitInfo.CONE1.unitThrow,
    mantle: unitInfo.CONE1.mantle,
    concave: unitInfo.CONE1.concave,
    modelName: unitInfo.CONE1.modelName,
  };

  const showFeederCard =
    unitInfo.modelName.includes('440E') || unitInfo.modelName === 'UH640';

  const requestUnitInfo: MobileRequestUnitInfo = {
    modelName: unitInfo.modelName,
    configKey: unitInfo.configuration.configKey,
    moisture: unitInfo.moisture,
    newMoisture: unitInfo.newMoisture,
    units: {
      CONE1: trackedCone1,
    },
  };

  if (unitInfo.modelName === 'US640E') {
    return {
      unitInfo: requestUnitInfo,
      type: UnitTypes.MOBILE_CONE_COMMON,
    };
  }

  if (showFeederCard) {
    return {
      type: UnitTypes.MOBILE_CONE_COMMON,
      unitInfo: {
        ...requestUnitInfo,
        units: {
          CONE1: trackedCone1,
          SCREEN1: {
            type: 'GENERIC_SCREEN',
            decks: unitInfo.feederScreen.feederScreenDecks.decks,
          },
          PAN_FEEDER1: {
            type: 'PAN_FEEDER_SP',
            modelName: unitInfo.feederScreen.feederModel,
          },
          FREEFALL_SCREEN1: {
            type: 'FREEFALL_SCREEN_SS',
            modelName: unitInfo.feederScreen.screenModel,
            decks: unitInfo.feederScreen.screenDecks.decks,
          },
        },
      },
    };
  }

  const decks =
    unitInfo.modelName !== 'QH441'
      ? unitInfo.SCREEN1.decks
      : {
          0: {
            separationSize: unitInfo.SCREEN1.decks[0].separationSize,
          },
        };
  const isUH440I = unitInfo.modelName.includes('UH440i');
  const showHangingScreen = isUH440I || (isUS && !showFeederCard);
  return {
    type: UnitTypes.MOBILE_CONE_COMMON,
    unitInfo: {
      ...requestUnitInfo,
      units: {
        CONE1: trackedCone1,
        SCREEN1: {
          type: 'GENERIC_SCREEN',
          decks: showHangingScreen ? unitInfo.hangingScreen.decks : decks,
        },
      },
    },
  };
};

export const getMobileWheeledConeRequest = (
  unitInfo: UnitInfoMobileConeInterface,
) => {
  const isCSWheeled = unitInfo.modelName.includes('US');

  const crusherTypeWheeled = isCSWheeled
    ? UnitTypes.CONE_CRUSHER_CS
    : UnitTypes.CONE_CRUSHER_CH;

  const CONE1 = {
    type: crusherTypeWheeled,
    css: unitInfo.CONE1.css,
    unitThrow: unitInfo.CONE1.unitThrow,
    mantle: unitInfo.CONE1.mantle,
    concave: unitInfo.CONE1.concave,
    modelName: unitInfo.CONE1.modelName,
  };

  const SPLIT1 = {
    type: 'SPLIT',
    splitType: 'PERCENT',
    amountRight:
      unitInfo.modelName === 'UH320_Flex'
        ? unitInfo.SPLIT1?.directToCrusher
        : unitInfo.SPLIT1?.returnToCrusher,
  };

  let units: SimulationUnits = {
    CONE1,
    SPLIT1,
  };

  if (unitInfo.modelName === 'UH320_Flex') {
    units = {
      ...units,
      SCREEN1: {
        type: 'GENERIC_SCREEN',
        decks: filterDecks(unitInfo.wheeledFlexScreen.feederScreenDecks, [0]),
      },
      SCREEN2: {
        type: 'GENERIC_SCREEN',
        decks: {
          0: {
            separationSize:
              unitInfo.wheeledFlexScreen.feederScreenDecks[1].separationSize,
          },
          1: {
            separationSize:
              unitInfo.wheeledFlexScreen.feederScreenDecks[2].separationSize,
          },
        },
      },

      FREEFALL_SCREEN_SF1: {
        type: 'FREEFALL_SCREEN_SF',
        modelName: 'SF1843',
        decks: filterDecks(unitInfo.wheeledFlexScreen.decks, [0, 1, 2]),
      },
    };
  } else if (unitInfo.modelName === 'UK373') {
    const decks = Object.values(
      unitInfo.wheeledScreenUK.CONVENTIONAL_SCREEN1.decks,
    ).reduce(
      (a, c, i) => ({
        ...a,
        [i]: {
          separationSize: c.separationSize,
          screeningMediaId: c.screeningMediaId?.value,
          overUnderSize: c.overUnderSize?.value,
        },
      }),
      {},
    );
    units = {
      ...units,
      CONVENTIONAL_SCREEN1: {
        type: 'CONVENTIONAL_SCREEN',
        screenFamily: 'SL',
        screenModel: unitInfo.wheeledScreenUK.CONVENTIONAL_SCREEN1.simModel,
        inclination: unitInfo.wheeledScreenUK.CONVENTIONAL_SCREEN1.inclination,
        materialType:
          unitInfo.wheeledScreenUK.CONVENTIONAL_SCREEN1.materialType.value,
        deckOneRelieving:
          unitInfo.wheeledScreenUK.CONVENTIONAL_SCREEN1.deckOneRelieving,
        decks,
      },
    };
  } else if (
    unitInfo.modelName === 'UH373' ||
    unitInfo.modelName === 'UH573' ||
    unitInfo.modelName === 'US373'
  ) {
    units = {
      CONE1,
    };
  } else {
    const decks =
      unitInfo.modelName === 'UH311' ||
      unitInfo.modelName === 'UH312' ||
      unitInfo.modelName === 'UH412'
        ? [0, 1, 2, 3]
        : [0, 1, 2];

    units = {
      ...units,
      CONVENTIONAL_SCREEN1: {
        type: 'CONVENTIONAL_SCREEN',
        screenFamily: 'SA',
        screenModel: unitInfo.wheeledScreen.CONVENTIONAL_SCREEN1.model,
        decks: filterDecks(
          unitInfo.wheeledScreen.CONVENTIONAL_SCREEN1.decks,
          decks,
        ),
      },
    };
  }

  return {
    type: UnitTypes.MOBILE_CONE_COMMON,
    unitInfo: {
      modelName: unitInfo.modelName,
      configKey: unitInfo.configuration.configKey,
      moisture: unitInfo.moisture,
      newMoisture: unitInfo.newMoisture,
      units: {
        ...units,
        CONE1,
      },
    },
  };
};

export const getMobileJawRequest = (unitInfo: UnitInfoMobileJawInterface) => {
  const uj473Conf3 =
    unitInfo.modelName === 'UJ473' && unitInfo.configuration.configKey === 3;
  const wheeledGrizzlyDecks1 = {
    0: {
      separationSize: unitInfo.GRIZZLY_FEEDER1.decks[0].separationSize,
    },
  };
  if (
    unitInfo.modelName.charAt(0) === 'U' &&
    unitInfo.modelName !== 'UJ440IVGFUS' &&
    unitInfo.modelName !== 'UJ443EVGFUS' &&
    unitInfo.modelName !== 'UJ473'
  ) {
    Object.assign(wheeledGrizzlyDecks1, {
      1: {
        separationSize: unitInfo.GRIZZLY_FEEDER1.decks[1].separationSize,
      },
    });
  }
  const wheeledGrizzlyDecks2 = {
    0: {
      separationSize: unitInfo.GRIZZLY_FEEDER2?.decks[0].separationSize,
    },
  };
  if (uj473Conf3) {
    Object.assign(wheeledGrizzlyDecks2, {
      1: {
        separationSize: unitInfo.GRIZZLY_FEEDER2?.decks[1].separationSize,
      },
    });
  }
  const feederScreenDecks = unitInfo.FEEDER_SCREEN1
    ? {
        0: {
          separationSize: unitInfo.FEEDER_SCREEN1.decks[0].separationSize,
        },
        1: {
          separationSize: unitInfo.FEEDER_SCREEN1.decks[1].separationSize,
        },
      }
    : undefined;
  const freefallDecks2D = {
    0: {
      separationSize: unitInfo.FREEFALL_SCREEN1?.decks[0].separationSize,
      screeningMediaId: 'WIRE_SQUARE',
    },
    1: {
      separationSize: unitInfo.FREEFALL_SCREEN1?.decks[1].separationSize,
      screeningMediaId: 'WIRE_SQUARE',
    },
  };

  const freefallDecks3D = {
    0: {
      separationSize: unitInfo.FREEFALL_SCREEN1?.decks[0].separationSize,
      screeningMediaId: 'PLATE',
    },
    1: {
      separationSize: unitInfo.FREEFALL_SCREEN1?.decks[1].separationSize,
      screeningMediaId: 'WIRE_SQUARE',
    },
    2: {
      separationSize: unitInfo.FREEFALL_SCREEN1?.decks[2].separationSize,
      screeningMediaId: 'WIRE_SQUARE',
    },
  };

  return {
    type: UnitTypes.MOBILE_JAW_COMMON,
    unitInfo: {
      modelName: unitInfo.modelName,
      configKey: unitInfo.configuration.configKey,
      moisture: unitInfo.moisture,
      newMoisture: unitInfo.newMoisture,
      units: {
        JAW1: {
          css: unitInfo.JAW1.css,
          type: 'JAW_CRUSHER_CJ',
          modelName: unitInfo.JAW1.modelName.simulationModel,
        },
        GRIZZLY_FEEDER1: {
          ...unitInfo.GRIZZLY_FEEDER1,
          decks: wheeledGrizzlyDecks1,
          type: 'GRIZZLY_FEEDER',
          modelName: unitInfo.GRIZZLY_FEEDER1.modelName.simulationModel,
        },
        GRIZZLY_FEEDER2: {
          ...unitInfo.GRIZZLY_FEEDER2,
          decks: wheeledGrizzlyDecks2,
          type: 'GRIZZLY_FEEDER',
          modelName: unitInfo.GRIZZLY_FEEDER2?.modelName.simulationModel,
          inclination: 8,
        },
        FEEDER_SCREEN1: {
          type: 'FEEDER_FEEDER_SCREEN_ST',
          modelName: unitInfo.FEEDER_SCREEN1?.modelName.simulationModel,
          inclination: 0,
          decks: feederScreenDecks,
        },
        FREEFALL_SCREEN1: {
          ...unitInfo.FREEFALL_SCREEN1,
          type: 'FREEFALL_SCREEN_SS',
          modelName: unitInfo.FREEFALL_SCREEN1?.model,
          decks:
            unitInfo.modelName === 'UJ473' &&
            unitInfo.configuration.configKey < 2
              ? freefallDecks3D
              : freefallDecks2D,
        },
        FREEFALL_SCREEN2: {
          ...unitInfo.FREEFALL_SCREEN2,
          type: 'FREEFALL_SCREEN_SS',
          modelName: unitInfo.FREEFALL_SCREEN2?.model,
          decks: freefallDecks2D,
        },
        SCREEN1: {
          ...unitInfo.SCREEN1,
          type: 'GENERIC_SCREEN',
        },
      },
    },
  };
};

export const getMobileTrackedScreenRequest = (
  unitInfo: UnitInfoMobileScreenInterface,
) => {
  let units: SimulationUnits = {
    GENERIC_SCREEN1: getGenericScreen1(unitInfo),
    CONVENTIONAL_SCREEN1: getConventionalScreen1(
      unitInfo,
      UnitTypes.MOBILE_TRACKED_SCREEN,
    ),
  };

  if (unitInfo.modelName === 'QA452' || unitInfo.modelName === 'QA442') {
    units = {
      ...units,
      SPLIT1: {
        type: 'SPLIT',
        amountLeft: 100 - unitInfo.SPLIT1.amountRight,
        splitType: 'PERCENT',
      },
    };
  }

  return {
    type: UnitTypes.MOBILE_SCREEN_COMMON,
    unitInfo: {
      modelName: unitInfo.modelName,
      configKey: unitInfo.configuration.configKey,
      moisture: unitInfo.moisture,
      newMoisture: unitInfo.newMoisture,
      units,
    },
  };
};

export const getMobileWheeledScreen = (
  unitInfo: UnitInfoMobileScreenInterface,
) => {
  let cleanedMobileWheeledUnitInfo: Omit<MobileRequestUnitInfo, 'units'> & {
    units?: SimulationUnits;
  } = {
    modelName: unitInfo.modelName,
    configKey: unitInfo.configuration.configKey,
    moisture: unitInfo.moisture,
    newMoisture: unitInfo.newMoisture,
  };

  const getGenericScreenDeck = (
    screen: 'GENERIC_SCREEN1' | 'GENERIC_SCREEN2',
    i: 0 | 1,
  ) => ({
    separationSize: unitInfo[screen].decks[i].separationSize,
  });

  if (unitInfo.modelName === 'UF373' || unitInfo.modelName === 'UF473') {
    const getDeck = (i: 0 | 1 | 2) => ({
      separationSize: unitInfo.CONVENTIONAL_SCREEN1.decks[i].separationSize,
      screeningMediaId:
        unitInfo.CONVENTIONAL_SCREEN1.decks[i].screeningMediaId?.value,
      overUnderSize:
        unitInfo.CONVENTIONAL_SCREEN1.decks[i].overUnderSize?.value,
    });
    const split = (i: 'SPLIT1' | 'SPLIT2' | 'SPLIT3') => ({
      type: 'SPLIT',
      amountLeft: 100 - unitInfo[i].amountRight,
      splitType: 'PERCENT',
    });

    cleanedMobileWheeledUnitInfo = {
      ...cleanedMobileWheeledUnitInfo,
      units: {
        CONVENTIONAL_SCREEN1: {
          type: 'CONVENTIONAL_SCREEN',
          screenFamily: 'SL',
          screenModel: unitInfo.CONVENTIONAL_SCREEN1.simModel,
          inclination: unitInfo.CONVENTIONAL_SCREEN1.inclination,
          materialType: unitInfo.CONVENTIONAL_SCREEN1.materialType.value,
          deckOneRelieving: unitInfo.CONVENTIONAL_SCREEN1.deckOneRelieving,
          decks: {
            0: getDeck(0),
            1: getDeck(1),
            2: getDeck(2),
          },
        },
        SPLIT1: split('SPLIT1'),
        SPLIT2: split('SPLIT2'),
        SPLIT3: split('SPLIT3'),
      },
    };
  } else if (unitInfo.configuration.configKey <= 3) {
    cleanedMobileWheeledUnitInfo = {
      ...cleanedMobileWheeledUnitInfo,
      units: {
        FREEFALL_SCREEN_SF1: getConventionalScreen1(
          unitInfo,
          UnitTypes.MOBILE_WHEELED_SCREEN,
        ),
        GENERIC_SCREEN1: {
          type: 'GENERIC_SCREEN',
          decks: {
            0: getGenericScreenDeck('GENERIC_SCREEN1', 0),
          },
        },
        GENERIC_SCREEN2: {
          type: 'GENERIC_SCREEN',
          decks: {
            0: getGenericScreenDeck('GENERIC_SCREEN2', 0),
            1: getGenericScreenDeck('GENERIC_SCREEN2', 1),
          },
        },
      },
    };
  } else {
    cleanedMobileWheeledUnitInfo = {
      ...cleanedMobileWheeledUnitInfo,
      units: {
        FREEFALL_SCREEN_SF1: getConventionalScreen1(
          unitInfo,
          UnitTypes.MOBILE_WHEELED_SCREEN,
        ),
        GENERIC_SCREEN1: {
          type: 'GENERIC_SCREEN',
          decks: {
            0: getGenericScreenDeck('GENERIC_SCREEN2', 0),
            1: getGenericScreenDeck('GENERIC_SCREEN2', 1),
          },
        },
      },
    };
  }

  return {
    type: UnitTypes.MOBILE_SCREEN_COMMON,
    unitInfo: cleanedMobileWheeledUnitInfo,
  };
};

export const getMobileHSIRequest = (unitInfo: UnitInfoMobileHSIInterface) => {
  const requestUnitInfo: MobileRequestUnitInfo = {
    modelName: unitInfo.modelName,
    configKey: unitInfo.configuration.configKey,
    moisture: unitInfo.moisture,
    newMoisture: unitInfo.newMoisture,
    units: {
      HSI1: {
        type: 'IMPACT_CRUSHER_CI',
        ...unitInfo.HSI1,
      },
      SCREEN1: {
        type: 'GENERIC_SCREEN',
        ...unitInfo.SCREEN1,
      },
    },
  };
  const includeHangingScreenTracked = unitInfo.configuration.configKey > 2;
  if (includeHangingScreenTracked) {
    const decks =
      unitInfo.modelName !== 'QI341'
        ? { ...unitInfo.SCREEN2.decks }
        : {
            0: {
              separationSize: unitInfo.SCREEN2.decks[0].separationSize,
            },
          };

    requestUnitInfo.units.SCREEN2 = {
      type: 'GENERIC_SCREEN',
      decks: decks,
    };
  }

  return {
    type: UnitTypes.MOBILE_HSI_COMMON,
    unitInfo: requestUnitInfo,
  };
};

export const getMobileVSIRequest = (unitInfo: UnitInfoMobileVSIInterface) => {
  const isFlex = unitInfo.modelName === 'UV320E_FLEX';
  const isDirect = unitInfo.modelName === 'UV320E_DIRECT';
  const isUV312 = unitInfo.modelName === 'UV312';
  const vsi1 = {
    type: 'IMPACT_CRUSHER_CV',
    ...unitInfo.VSI1,
    materialShape: unitInfo.VSI1.materialShape.value,
    onMobile: true,
  };

  const getSplit = (val: 'returnToCrusher' | 'directToCrusher') => ({
    type: 'SPLIT',
    splitType: 'PERCENT',
    amountRight: unitInfo.SPLIT1[val],
  });

  const getScreenDeck = (i: 0 | 1 | 2) => ({
    separationSize: unitInfo.SCREEN1.decks[i].separationSize,
  });

  let units: SimulationUnits = {
    VSI1: vsi1,
  };

  if (!isDirect && !isFlex) {
    if (isUV312) {
      units = {
        ...units,
        CONVENTIONAL_SCREEN1: {
          ...unitInfo.SCREEN1,
          type: 'CONVENTIONAL_SCREEN',
          screenFamily: 'SA',
        },
        SPLIT1: getSplit('returnToCrusher'),
      };
    } else {
      units = {
        ...units,
        CONVENTIONAL_SCREEN1: {
          type: 'CONVENTIONAL_SCREEN',
          screenFamily: 'SA',
          screenModel: unitInfo.SCREEN1.screenModel,
          decks: {
            0: getScreenDeck(0),
            1: getScreenDeck(1),
            2: getScreenDeck(2),
          },
        },
        SPLIT1: getSplit('returnToCrusher'),
      };
    }
  } else if (isDirect) {
    units = {
      ...units,
      FREEFALL_SCREEN_SF1: {
        ...unitInfo.FREEFALL_SCREEN1,
        type: 'FREEFALL_SCREEN_SF',
      },
    };
  } else {
    units = {
      ...units,
      SCREEN1: {
        type: 'GENERIC_SCREEN',
        decks: {
          0: getScreenDeck(0),
        },
      },
      SCREEN2: {
        ...unitInfo.SCREEN2,
        type: 'GENERIC_SCREEN',
      },
      FREEFALL_SCREEN_SF1: {
        ...unitInfo.FREEFALL_SCREEN1,
        type: 'FREEFALL_SCREEN_SF',
      },
      SPLIT1: getSplit('directToCrusher'),
    };
  }

  return {
    type: UnitTypes.MOBILE_VSI,
    unitInfo: {
      modelName: unitInfo.modelName,
      configKey: unitInfo.configuration.configKey,
      moisture: unitInfo.moisture,
      newMoisture: unitInfo.newMoisture,
      units,
    },
  };
};

export const getMobileJawAndConeRequest = (
  unitInfo: UnitInfoMobileJawAndConeInterface,
) => {
  return {
    type: UnitTypes.MOBILE_JAW_AND_CONE,
    unitInfo: {
      modelName: unitInfo.modelName,
      configKey: unitInfo.configuration.configKey,
      moisture: unitInfo.moisture,
      newMoisture: unitInfo.newMoisture,
      units: {
        CONE1: {
          type: 'CONE_CRUSHER_CH',
          ...unitInfo.CONE1,
        },
        JAW1: {
          type: 'JAW_CRUSHER_CJ',
          ...unitInfo.JAW1,
        },
        GRIZZLY_FEEDER1: {
          type: 'GRIZZLY_FEEDER',
          ...unitInfo.GRIZZLY_FEEDER1,
        },
        CONVENTIONAL_SCREEN1: {
          type: 'CONVENTIONAL_SCREEN',
          screenFamily: 'SA',
          screenModel: 'SA1542',
          decks: unitInfo.CONVENTIONAL_SCREEN1.decks,
        },
        CONVENTIONAL_SCREEN2: {
          type: 'CONVENTIONAL_SCREEN',
          screenFamily: 'SA',
          screenModel: 'SA1542',
          decks: unitInfo.CONVENTIONAL_SCREEN2.decks,
        },
        CONVENTIONAL_SCREEN3: {
          type: 'CONVENTIONAL_SCREEN',
          screenFamily: 'SA',
          screenModel: 'SA1542',
          decks: unitInfo.CONVENTIONAL_SCREEN3.decks,
        },
      },
    },
  };
};
